import React, { useState, useEffect, useRef } from 'react';

const Input = ({
  label,
  name,
  type = 'text',
  value = '',
  onChange,
  placeholder = '',
  required = false,
  disabled = false,
  mask,
  helperText,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}) => {
  // Estado interno para controlar o valor do input
  const [internalValue, setInternalValue] = useState(value || '');
  const inputRef = useRef(null);
  
  // Sincroniza o valor interno com o prop value quando ele muda externamente
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value || '');
    }
  }, [value]);

  // Função de máscara simplificada e robusta
  const applyMask = (inputValue, maskPattern) => {
    if (!maskPattern || !inputValue) return inputValue;
    
    const cleanValue = inputValue.replace(/\D/g, '');
    let result = '';
    let dataIndex = 0;
    
    for (let i = 0; i < maskPattern.length && dataIndex < cleanValue.length; i++) {
      if (maskPattern[i] === '9') {
        result += cleanValue[dataIndex];
        dataIndex++;
      } else {
        result += maskPattern[i];
      }
    }
    
    return result;
  };

  // Handler principal do input
  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    let processedValue = rawValue;
    
    // Aplicar máscara se existir
    if (mask) {
      processedValue = applyMask(rawValue, mask);
    }
    
    // Atualizar estado interno imediatamente
    setInternalValue(processedValue);
    
    // Chamar onChange do pai com delay mínimo para evitar conflitos
    if (onChange) {
      // Usar setTimeout para garantir que o estado seja atualizado primeiro
      setTimeout(() => {
        onChange({
          target: {
            name: name,
            value: processedValue
          }
        });
      }, 0);
    }
  };

  // Preservar posição do cursor para máscaras
  const preserveCursorPosition = (e) => {
    const cursorPosition = e.target.selectionStart;
    const inputLength = e.target.value.length;
    
    setTimeout(() => {
      if (inputRef.current && cursorPosition !== null) {
        const newLength = inputRef.current.value.length;
        const newPosition = Math.min(cursorPosition + (newLength - inputLength), newLength);
        inputRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const inputProps = {
    ref: inputRef,
    type: type,
    id: name,
    name: name,
    value: internalValue,
    onChange: handleInputChange,
    onInput: preserveCursorPosition,
    placeholder: placeholder,
    required: required,
    disabled: disabled,
    autoComplete: 'off',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: 'false',
    className: `
      w-full px-3 py-2 border border-neutral-300 rounded-lg 
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
      disabled:bg-neutral-50 disabled:text-neutral-500
      ${leftIcon ? 'pl-10' : ''}
      ${rightIcon ? 'pr-10' : ''}
      ${className}
    `.replace(/\s+/g, ' ').trim(),
    ...props
  };

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={name} 
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-400">
            {leftIcon}
          </div>
        )}
        
        <input {...inputProps} />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {rightIcon}
          </div>
        )}
      </div>
      
      {helperText && (
        <p className="mt-1 text-sm text-neutral-500">{helperText}</p>
      )}
    </div>
  );
};

export default Input;